import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { Bar } from 'react-chartjs-2'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { useSelector } from 'react-redux'

import GraphContainer from '../GraphContainer'

import { getOptions } from './getOptions'
import { insertIf } from '../../../../../helpers/common'
import { formatNumberToShort } from '../../../helpers'

import { selectedPartnershipCurrency } from '../../../../../modules/selectors/partnerships'
import { socialReturnOnInvestmentReportSelector } from '../../../../../modules/selectors/socialReturnOnInvestment'

const TotalSocialImpactGraph = () => {
  const { t } = useTranslation()

  const { currency_symbol: currencySymbol } = useSelector(selectedPartnershipCurrency)
  const { totalImpact, financialImpact, healthImpact, wellbeingImpact } = useSelector(
    socialReturnOnInvestmentReportSelector
  )

  const impactGraphData = useMemo(() => {
    return [
      ...insertIf(healthImpact, healthImpact),
      ...insertIf(wellbeingImpact, wellbeingImpact),
      ...insertIf(financialImpact, financialImpact)
    ]
  }, [financialImpact, healthImpact, wellbeingImpact])

  const maxImpactValue = useMemo(() => Math.max(...impactGraphData), [impactGraphData])

  const impactGraphLabels = useMemo(() => {
    return [
      ...insertIf(healthImpact, 'Health'),
      ...insertIf(wellbeingImpact, 'Wellbeing'),
      ...insertIf(financialImpact, 'Financial')
    ]
  }, [financialImpact, healthImpact, wellbeingImpact])

  const impactGraphDatasets = useMemo(
    () => [
      {
        label: 'Total Social Impact',
        data: impactGraphData,
        backgroundColor: ['#2A62C6', '#4C82CE', '#7EABDE'],
        borderRadius: {
          topLeft: 0,
          topRight: 4,
          bottomLeft: 0,
          bottomRight: 4
        },

        borderSkipped: false,
        maxBarThickness: 45
      }
    ],
    [impactGraphData]
  )

  const graphData = useMemo(
    () => ({
      labels: impactGraphLabels,
      datasets: impactGraphDatasets
    }),
    [impactGraphLabels, impactGraphDatasets]
  )

  const options = useMemo(() => getOptions(maxImpactValue, currencySymbol), [maxImpactValue, currencySymbol])

  const plugins = useMemo(() => [ChartDataLabels], [])

  const formattedTotalImpact = useMemo(() => {
    return `${currencySymbol}${formatNumberToShort(totalImpact)}`
  }, [currencySymbol, totalImpact])

  return (
    <GraphContainer title={t('Total Social Impact - by Impact Pathway')} totalValue={formattedTotalImpact}>
      <Bar type="bar" data={graphData} options={options} plugins={plugins} />
    </GraphContainer>
  )
}

export default TotalSocialImpactGraph
